import { useState } from "react";
import type { FormEvent } from "react";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  CORES_DO_SINALIZADOR,
  ROTULO_DA_COR,
  TINTA_DA_COR,
  useAlternarFavorito,
  useDefinirSinalizador,
  useRetirarSinalizador,
  type CorDoSinalizador,
} from "../../nucleo/operacao";
import type { Ticket } from "../../nucleo/tickets";
import { Aviso, Botao, Campo, Cartao } from "../ui";

/** A estrela e a bandeira de um chamado na fila: só um sinal, sem ação (a ação é no chamado). */
export function IndicadorDeMarcas({ ticket }: { ticket: Pick<Ticket, "favorito" | "sinalizador"> }) {
  if (!ticket.favorito && !ticket.sinalizador) return null;
  return (
    <span className="inline-flex items-center gap-1" data-testid="marcas-na-fila">
      {ticket.favorito ? <span role="img" aria-label="Favorito">★</span> : null}
      {ticket.sinalizador ? (
        <span
          role="img"
          aria-label={`Sinalizador ${ROTULO_DA_COR[ticket.sinalizador.cor].toLocaleLowerCase("pt-BR")}: ${ticket.sinalizador.motivo}`}
          title={ticket.sinalizador.motivo}
          style={{ color: TINTA_DA_COR[ticket.sinalizador.cor] }}
        >
          ⚑
        </span>
      ) : null}
    </span>
  );
}

/**
 * Favorito (pessoal) e sinalizador (da equipe) do chamado. Só o lado interno chega aqui; quem não
 * pode marcar (`ticket:marcar`) vê a bandeira, sem os controles.
 */
export function MarcasDoChamado({ ticket, podeMarcar }: { ticket: Ticket; podeMarcar: boolean }) {
  const favoritar = useAlternarFavorito(ticket.id);
  const definir = useDefinirSinalizador(ticket.id);
  const retirar = useRetirarSinalizador(ticket.id);
  const [cor, setCor] = useState<CorDoSinalizador>("vermelho");
  const [motivo, setMotivo] = useState("");
  const marca = ticket.sinalizador;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    definir.mutate({ cor, motivo: motivo.trim() }, { onSuccess: () => setMotivo("") });
  }

  return (
    <Cartao titulo="Marcas da equipe" className="mb-4" data-testid="marcas-do-chamado">
      <p className="nota-rodape mb-3">O cliente não vê favoritos nem sinalizadores.</p>
      {podeMarcar ? (
        <Botao
          variante="fantasma"
          aria-pressed={Boolean(ticket.favorito)}
          disabled={favoritar.isPending}
          onClick={() => favoritar.mutate(!ticket.favorito)}
        >
          {ticket.favorito ? "★ Remover dos favoritos" : "☆ Favoritar"}
        </Botao>
      ) : null}
      {marca ? (
        <p className="mt-3" data-testid="sinalizador-atual">
          <span style={{ color: TINTA_DA_COR[marca.cor] }}>⚑ {ROTULO_DA_COR[marca.cor]}</span>
          {" · "}
          {marca.motivo}
          {podeMarcar ? (
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              className="ml-3"
              disabled={retirar.isPending}
              onClick={() => retirar.mutate()}
            >
              Retirar sinalizador
            </Botao>
          ) : null}
        </p>
      ) : (
        <p className="nota-rodape mt-3">Nenhum sinalizador neste chamado.</p>
      )}
      {podeMarcar ? (
        <form className="mt-3 flex flex-wrap items-end gap-3" onSubmit={enviar}>
          <label className="campo" htmlFor="cor-do-sinalizador">
            <span className="campo__rotulo">Cor</span>
            <select
              id="cor-do-sinalizador"
              className="campo__controle"
              value={cor}
              onChange={(evento) => setCor(evento.target.value as CorDoSinalizador)}
            >
              {CORES_DO_SINALIZADOR.map((item) => (
                <option value={item} key={item}>{ROTULO_DA_COR[item]}</option>
              ))}
            </select>
          </label>
          <Campo
            rotulo="Motivo"
            value={motivo}
            onChange={(evento) => setMotivo(evento.target.value)}
            maxLength={200}
            placeholder="Ex.: cliente aguardando peça"
          />
          <Botao type="submit" disabled={!motivo.trim() || definir.isPending}>
            {marca ? "Trocar sinalizador" : "Sinalizar"}
          </Botao>
        </form>
      ) : null}
      {[favoritar, definir, retirar].map((acao, indice) =>
        acao.isError ? <Aviso tom="erro" className="mt-3" key={indice}>{mensagemDoConsole(acao.error)}</Aviso> : null,
      )}
    </Cartao>
  );
}
